'use client';
import './header.scss';

import MenuIcon from '@mui/icons-material/Menu';
import SatelliteOutlinedIcon from '@mui/icons-material/SatelliteOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
/* eslint-disable @next/next/no-img-element */
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Toolbar,
} from '@mui/material';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
/* import react and component */
import { MouseEvent, useEffect, useState } from 'react';

import { MixpanelTracking } from '@/services/mixpanel';

import LogoHub from '@/assets/img/hub-logo.svg';
import Logo from '@/assets/img/sw-logo-explore.svg';

import { platformApps } from '@/constants/enums/enums';
import { ROLES } from '@/constants/enums/userManagement';
import {
  Page,
  protectedPagesExplore,
  protectedPagesHub,
  publicPagesExplore,
  publicPagesHub,
} from '@/constants/routes';
import { useSpinnerContext } from '@/context/SpinnerContext';
import { triggerEvent } from '@/lib/events';
import { filterHubNavPages } from '@/lib/nav/filterHubNavPages';
import {
  cleanupForLogout,
  getDeviceType,
  logoutChannel,
  routeBasedEventName,
  routeBasedEventNameExplore,
} from '@/lib/utils';
import { getProfile_rh } from '@/network/routeHandlerService/orderNew';

import PublicIcon from '@mui/icons-material/Public';
import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';
import Accountsdrawer from '../accounts/accounts';
import CartDrawer from '../cart/cartDrawer/cartDrawer';
import HelpAndSupport from '../helpAndSupport/helpAndSupport';
import LanguageChanger from './LanguageSwitcher';
import { checkUserSession } from '@/network/serverApis/login';
import customToaster from '../toast/CustomToaster';
import { useAuthenticationContext } from '@/context/authenticationProvider';
import AgentPanelToggle from '@/features/hub-agent/components/AgentPanel/AgentPanelToggle';
const iconMap: any = {
  SatelliteOutlinedIcon: SatelliteOutlinedIcon,
  UpdateOutlinedIcon: UpdateOutlinedIcon,
  PublicIcon: PublicIcon,
  SatelliteAltOutlinedIcon: SatelliteAltOutlinedIcon,
  SettingsOutlinedIcon: SettingsOutlinedIcon,
};

const protectedPages =
  process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.HUB
    ? protectedPagesHub
    : protectedPagesExplore;

if (typeof window !== 'undefined' && !posthog.__loaded && process.env.NEXT_PUBLIC_POSTHOG_KEY) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host:
      process.env.NEXT_PUBLIC_ENABLE_REWRITE === 'true'
        ? process.env.NEXT_PUBLIC_POSTHOG_PROXY_URL
        : process.env.NEXT_PUBLIC_POSTHOG_HOST,
    ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,

    capture_pageview: true,
    autocapture: false,
    //this is in debugging mode remove this to use in production
    debug: process.env.NEXT_PUBLIC_POSTHOG_DEBUG_VALUE === 'true',
    loaded: (posthog: any) => {
      const deviceTypeValue = getDeviceType();
      posthog.setPersonPropertiesForFlags({
        origin: process.env.NEXT_PUBLIC_PLATFORM_APP,
        $device_type: deviceTypeValue,
      });
    },
  });
}
// posthog.register({origin:'explore'})
// posthog.reloadFeatureFlags();

export default function Header({
  secrets,
}: {
  secrets: {
    intercomAppId: string | undefined;
  };
}) {
  const router = useRouter();
  const path = usePathname();
  const searchParams = useSearchParams();
  // const { user, isLoading } = useUser();
  const { userData, setUserData, isLoading } = useAuthenticationContext();
  const [pages, setPages] = useState(
    process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.HUB ? publicPagesHub : publicPagesExplore
  );
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [protectedPage, setProtectedPage] = useState<Page[]>(protectedPages);

  const { startLoaderEvent, stopLoaderEvent } = useSpinnerContext();
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [showNav, setShowNav] = useState(
    process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.HUB ? true : false
  );

  const { t } = useTranslation('header');
  const isHUB = process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.HUB ? true : false;
  useEffect(() => {
    //add a broadcast listener for all tabs
    logoutChannel.onmessage = () => {
      router.push('/auth/logout');
      logoutChannel.close();
    };
    if (!userData && !isLoading) {
      cleanupForLogout(router, MixpanelTracking);
      setUserData(null);
    }
    () => {
      logoutChannel.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, isLoading]);

  const getUserSession = async () => {
    try {
      triggerEvent(startLoaderEvent);

      // eslint-disable-next-line require-use-server
      const checkUserLogin = await checkUserSession();
      setIsUserLoggedIn(checkUserLogin ? true : false);
      if (process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.EXPLORE) {
        if (checkUserLogin) {
          sessionStorage.removeItem('show_nav');
          const hasNavParam = searchParams.has('nav');
          if (hasNavParam) {
            window.location.href = path;
          }
          setShowNav(true);
        } else {
          const sessionStorageNav = sessionStorage.getItem('show_nav') === 'false' ? false : true;
          const showNavCheck = searchParams.get('nav') === 'false' ? false : sessionStorageNav;
          if (searchParams.get('nav') === 'false') {
            sessionStorage.setItem('show_nav', 'false');
          }
          setShowNav(showNavCheck);
        }
      }

      return checkUserLogin ? true : false;
    } catch (err: any) {
      customToaster(t('common:generalError'));
    } finally {
      triggerEvent(stopLoaderEvent);
    }
  };

  useEffect(() => {
    getUserSession();

    async function setProfileBasePage() {
      let projectCollectionsEnabled: any = null;
      // let hubTeaserTabNameVariant: any = 'control';
      let profile: any = null;
      if (process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.HUB) {
        profile = await getProfile_rh();
      }

      posthog.onFeatureFlags(() => {
        projectCollectionsEnabled = posthog.getFeatureFlag('enable_project_collections');
        // hubTeaserTabNameVariant = posthog.getFeatureFlag('hub_teaser_tab_name');
        if (process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.HUB) {
          const role = profile?.multi_user_account?.user?.role;
          const newPage = filterHubNavPages(protectedPages, {
            approvalEnabled: !!profile?.multi_user_account?.organization?.approval_enabled,
            isReader: role === ROLES.READER,
            isCreator: role === ROLES.CREATOR,
            projectCollectionsEnabled: !!projectCollectionsEnabled,
            processingEnabled: posthog.isFeatureEnabled('enable_hub_processing') === true,
          });

          setProtectedPage(newPage);
          setShowSkeleton(false);
        } else {
          // const updatedExplorePages = getUpdatedNamedPagesForExploreABTest(
          //   publicPagesExplore,
          //   hubTeaserTabNameVariant
          // );

          // const updatedExploreProtectedPages = getUpdatedNamedPagesForExploreABTest(
          //   protectedPages,
          //   hubTeaserTabNameVariant
          // );
          setPages(publicPagesExplore);
          setProtectedPage(protectedPages);
          setShowSkeleton(false);
        }
      });
    }
    path !== '/unauthorized' && path !== '/verify-email' ? setProfileBasePage() : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };
  return (
    <>
      {path !== '/unauthorized' && (
        <AppBar position="static" className="app-header">
          <Toolbar>
            {showNav && (
              <Box className="toggle" sx={{ flexGrow: 1, display: { xs: 'flex', lg: 'none' } }}>
                <IconButton
                  name="Menu"
                  size="medium"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  MenuListProps={{
                    className: 'mbl-menu',
                  }}
                  sx={{
                    display: { xs: 'block', lg: 'none' },
                  }}
                >
                  {isUserLoggedIn
                    ? protectedPage.map(page => {
                        const IconComponent = page?.icon && iconMap[page?.icon];
                        return (
                          <MenuItem
                            className="mbl-menu-list"
                            key={page?.key}
                            onClick={handleCloseNavMenu}
                          >
                            <Link
                              onClick={() => {
                                page.href !== path && triggerEvent(startLoaderEvent);
                                router.push(page?.href);
                                if (isHUB) {
                                  const eventName = routeBasedEventName(page.href);
                                  eventName && MixpanelTracking.getInstance().track(eventName);
                                } else {
                                  const eventNameExplore = routeBasedEventNameExplore(page.href);
                                  eventNameExplore &&
                                    MixpanelTracking.getInstance().track(eventNameExplore);
                                }
                              }}
                            >
                              {page.iconClass ? <i className={page.icon}></i> : <IconComponent />}
                              {t(page.tkey)}
                            </Link>
                          </MenuItem>
                        );
                      })
                    : pages.map(page => {
                        const IconComponent = page?.icon && iconMap[page.icon];
                        return (
                          <MenuItem
                            className="mbl-menu-list"
                            key={page.key}
                            onClick={handleCloseNavMenu}
                          >
                            <Link
                              onClick={() => {
                                page.href !== path && triggerEvent(startLoaderEvent);
                                router.push(page.href);
                                if (isHUB) {
                                  const eventName = routeBasedEventName(page.href);
                                  eventName && MixpanelTracking.getInstance().track(eventName);
                                } else {
                                  const eventNameExplore = routeBasedEventNameExplore(page.href);
                                  eventNameExplore &&
                                    MixpanelTracking.getInstance().track(eventNameExplore);
                                }
                              }}
                            >
                              {page.iconClass ? <i className={page.icon}></i> : <IconComponent />}
                              {t(page.tkey)}
                            </Link>
                          </MenuItem>
                        );
                      })}
                </Menu>
              </Box>
            )}

            <Box className="header-logo">
              <Link
                onClick={() => {
                  if (process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.EXPLORE) {
                    if (!showNav) {
                      return;
                    }
                  }
                  router.push('/');
                }}
              >
                {process.env.NEXT_PUBLIC_PLATFORM_APP === platformApps.HUB ? (
                  <img src={LogoHub.src} alt="Hub Logo" />
                ) : (
                  <img src={Logo.src} alt="Explore Logo" />
                )}
              </Link>
            </Box>

            {showNav && (
              <>
                <Box
                  className="desktop-menu"
                  sx={{ flexGrow: 1, display: { xs: 'none', lg: 'flex' } }}
                >
                  {isUserLoggedIn
                    ? protectedPage.map(page => {
                        if (page?.href === '/my-orders' || page?.href === '/fr-CA/my-orders') {
                          router.prefetch(page.href);
                        }
                        const IconComponent = iconMap[page?.icon];
                        return showSkeleton ? (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                            className="desktop-skeleton"
                            key={page.key}
                          >
                            <Skeleton variant="rounded" height={18} width={18} />
                            <Skeleton height={20} width={110} />
                          </Stack>
                        ) : (
                          <Button
                            name={`${page.tkey}-button`}
                            className={page.href === path ? 'active' : ''}
                            key={page.key}
                            sx={{ my: 2, color: 'white', display: 'block' }}
                            onClick={() => {
                              page.href !== path && triggerEvent(startLoaderEvent);
                              router.push(page.href);
                              if (isHUB) {
                                const eventName = routeBasedEventName(page.href);
                                eventName && MixpanelTracking.getInstance().track(eventName);
                              } else {
                                const eventNameExplore = routeBasedEventNameExplore(page.href);
                                eventNameExplore &&
                                  MixpanelTracking.getInstance().track(eventNameExplore);
                              }
                            }}
                          >
                            {page?.iconClass ? <i className={page.icon}></i> : <IconComponent />}
                            {t(page.tkey)}
                          </Button>
                        );
                      })
                    : pages.map(page => {
                        const IconComponent = iconMap[page.icon];
                        return (
                          <Button
                            name={`${page.tkey}-button`}
                            className={page.href === path ? 'active' : ''}
                            key={page.key}
                            sx={{ my: 2, color: 'white', display: 'block' }}
                            onClick={() => {
                              page.href !== path && triggerEvent(startLoaderEvent);
                              router.push(page.href);
                              if (isHUB) {
                                const eventName: any = routeBasedEventName(page.href);
                                eventName && MixpanelTracking.getInstance().track(eventName);
                              } else {
                                const eventNameExplore: any = routeBasedEventNameExplore(page.href);
                                MixpanelTracking.getInstance().track(eventNameExplore);
                              }
                            }}
                          >
                            {page?.iconClass ? <i className={page.icon}></i> : <IconComponent />}
                            {t(page.tkey)}
                          </Button>
                        );
                      })}
                </Box>
                <Stack
                  className="header-actions"
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ flexGrow: 0 }}
                >
                  {process.env.NEXT_PUBLIC_SHOW_LANGUAGE_SWITCHER && <LanguageChanger />}
                  <AgentPanelToggle />
                  <CartDrawer path={path} />
                  <Accountsdrawer secrets={secrets} />
                  <HelpAndSupport />
                </Stack>
              </>
            )}
          </Toolbar>
        </AppBar>
      )}
    </>
  );
}
